import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import cn from 'classnames';
import { env } from 'env';
import { useAuthState } from 'contexts/auth-context';
import { useRegion } from 'contexts/RegionContext';
import { CustomIcon, Tooltip } from '@p81-common/ui';
import {
  Icon,
  IconCountry,
  Heading,
  TextWithTooltip,
  Text,
  WithTooltip,
  ButtonWithDropdown
} from 'components/common/Elements/Elements';
import BodyDropdownSetting from 'components/common/Elements/ButtonWithDropdown/parts/BodyDropdownSetting/BodyDropdownSetting';
import ActualStatusMarker from '../ActualStatusMarker/ActualStatusMarker';

import { links } from 'data/constants';
import { postRequest } from 'utils/requests';
import { getCloudActionErrorMessage } from 'utils/cloudManagement';
import { showNotif } from 'utils/notification';
import { access, resources, hasAccess } from 'utils/permissions';
import { getRestartOptions, getRequestBody } from './helpers/helpers';
import { capitalizeFirst } from 'utils/common';
import VersionSelectionModal from 'components/CS/NetworkRollout/parts/VersionSelectionModal';
import { ACTIONS } from 'components/CS/NetworkRollout/helpers/config';
import GwResizeModal, { PlanType } from './parts/GwResizeModal/GwResizeModal';

import classes from './NetworkWidget.module.scss';

interface NetworkWidgetProps {
  networkId: string;
  networkVersion?: string;
  networkComponent: any; // TODO: Define proper interface for network component
  customerId?: string;
  isRegion?: boolean;
  isGW?: boolean;
  isTunnel?: boolean;
  resize?: boolean;
  setShowProgressBar?: (show: boolean) => void;
  isCpuResizeEnabled?: boolean;
  regionName?: string;
}

interface ActionState {
  actionName?: string;
  actionKey?: string;
  requiresVersion?: boolean;
  version?: string;
  requiresSize?: boolean;
  instancePlanType?: PlanType;
}

const NetworkWidget: React.FC<NetworkWidgetProps> = ({
  networkId,
  networkVersion,
  networkComponent,
  customerId,
  isRegion = false,
  isGW = false,
  isTunnel = false,
  resize,
  setShowProgressBar,
  isCpuResizeEnabled,
  regionName
}) => {
  const { region } = useRegion();
  const hasCloudManagementAccessWriteAccess = hasAccess([
    resources.CLOUD_MANAGEMENT,
    access.WRITE
  ]);
  const [netComponent, setNetComponent] = useState(networkComponent);
  const [action, setAction] = useState<ActionState>({});
  const [isErrorStatus, setIsErrorStatus] = useState<boolean>(false);
  const [versionModalOpen, setVersionModalOpen] = useState<boolean>(false);
  const [resizeModalOpen, setResizeModalOpen] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<ActionState | null>(null);

  const grafanaGatewayUrl =
    env[`REACT_APP_GRAFANA_GATEWAY_URL_${region.code}`] ||
    env.REACT_APP_GRAFANA_GATEWAY_URL_US;

  let title: string = '';
  let text: string = '';
  let colorLine: string = '';
  let icon: string = '';
  let version: string = '';

  const isV4Network = networkVersion === '4.0.0';
  if (isRegion) {
    colorLine = !isV4Network ? 'reg' : `reg${netComponent.regionStatus}`;
    title = netComponent.providerRegionName;
    text = !isV4Network
      ? netComponent.regionDns
      : netComponent.regionPublicIp.join(', ');
  } else if (isGW) {
    colorLine = 'gat';
    icon = 'server';
    title = netComponent.gatewayDns;
    text = netComponent.gatewayIp;
    version = netComponent.gatewayImageVersion;
  } else if (isTunnel) {
    colorLine = 'tun';
    icon = netComponent.tunnelType === 'connector' ? 'connector' : 'tunnel';
    title = netComponent.tunnelName;
    text = netComponent.tunnelIp;
  }

  const getDropdownList = () => {
    const options = getRestartOptions(
      isGW,
      setAction,
      useAuthState().auth.user.role,
      action.actionName,
      title,
      startAction,
      isCpuResizeEnabled
    );
    return options || [];
  };
  const performStartAction = (actionToRun: any) => {
    if (setShowProgressBar) {
      setShowProgressBar(true);
    }
    let cancelled = false;
    const path = '/cloud/management/run-action';

    const REQ_BODY = getRequestBody(
      isGW,
      netComponent,
      customerId,
      actionToRun
    );
    postRequest(path, REQ_BODY)
      .then(res => {
        if (!cancelled) {
          if (!isEmpty(res.body)) {
            const beaconStatus = res.body;

            const newNetworkComponent = {
              ...netComponent,
              isBeaconStatusExist: true,
              beaconStatus: beaconStatus
            };
            setNetComponent(newNetworkComponent);
            if (setShowProgressBar) {
              setShowProgressBar(true);
            }
          }
        }
      })
      .catch(err => {
        if (!cancelled) {
          showNotif({
            message: getCloudActionErrorMessage(err),
            kind: 'error',
            dismissAfter: 8000,
            type: 'alert'
          });
          setIsErrorStatus(true);
          if (setShowProgressBar) {
            setShowProgressBar(false);
          }
        }
      });
    return () => (cancelled = true);
  };

  const startAction = (actionToStart: any) => {
    if (actionToStart.requiresVersion) {
      setPendingAction(actionToStart);
      setVersionModalOpen(true);
      return;
    }
    if (actionToStart.requiresSize) {
      setPendingAction(actionToStart);
      setResizeModalOpen(true);
      return;
    }
    performStartAction(actionToStart);
  };

  const handleResizeProceed = (instancePlanType: PlanType) => {
    setResizeModalOpen(false);
    if (pendingAction) {
      performStartAction({ ...pendingAction, instancePlanType });
      setPendingAction(null);
    }
  };

  const handleResizeClose = () => {
    setResizeModalOpen(false);
    setPendingAction(null);
  };

  const handleVersionProceed = (version: string) => {
    setVersionModalOpen(false);
    if (pendingAction) {
      const actionWithVersion = { ...pendingAction, version };
      performStartAction(actionWithVersion);
      setPendingAction(null);
    }
  };

  const handleVersionClose = () => {
    setVersionModalOpen(false);
    setPendingAction(null);
  };
  return (
    <>
      <VersionSelectionModal
        action={versionModalOpen ? ACTIONS.UPGRADE_GATEWAY : null}
        open={versionModalOpen}
        onClose={handleVersionClose}
        onProceed={handleVersionProceed}
      />
      <GwResizeModal
        open={resizeModalOpen}
        gatewayName={title}
        region={regionName}
        currentPlanType={netComponent.instancePlanType}
        availablePlanTypes={netComponent.availableInstancePlanTypes}
        onClose={handleResizeClose}
        onProceed={handleResizeProceed}
      />
      <div
        className={cn(
          classes.widjet,
          !isErrorStatus ? classes[colorLine] : classes.err,

          resize && classes.resize
        )}
      >
        <div className={classes.info}>
          <div
            className={cn(
              classes.info_wrap,
              !isRegion && classes.info_wrap__withButton
            )}
          >
            <div className={classes.flagWrap}>
              {isRegion ? (
                <IconCountry country={netComponent.regionCountry} size="40px" />
              ) : (
                <Icon size="20px" icon={icon} color={'#3a5ba2'} />
              )}
              {!isRegion && (
                <ActualStatusMarker
                  networkId={networkId}
                  networkComponent={netComponent}
                  componentType={isGW ? 'gw' : 'tunnel'}
                />
              )}
            </div>
            <div className={classes.overflow}>
              <div className={classes.overflow_header__wrap}>
                {isGW ? (
                  <Heading className={classes.overflow}>
                    <a
                      href={
                        grafanaGatewayUrl?.replace(
                          'nodeId',
                          netComponent.gatewayId
                        ) || ''
                      }
                      className={cn(classes.link)}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <TextWithTooltip
                        text={title}
                        saveNotification="activity:platform.TEXT_COPY"
                      />
                    </a>
                  </Heading>
                ) : (
                  <Heading className={classes.overflow}>
                    <TextWithTooltip
                      text={title}
                      saveNotification="activity:platform.TEXT_COPY"
                    />
                  </Heading>
                )}
                <div className={classes.regionsAddData}>
                  {isRegion && isV4Network && (
                    <Heading className={classes.overflow}>
                      <Text
                        className={cn(
                          classes.regionsAddData,
                          classes[`regionsAddData_${netComponent.regionStatus}`]
                        )}
                        type="s"
                        weight="m"
                        size={6}
                      >
                        {capitalizeFirst(netComponent.regionStatus)}
                      </Text>
                      <Text
                        className={cn(
                          classes.regionsAddData,
                          classes.regionsAddData_grey
                        )}
                        type="s"
                        weight="m"
                        size={6}
                      >
                        {`SU: ${netComponent.regionScaleUnits}`}
                      </Text>
                    </Heading>
                  )}
                </div>
                {isTunnel && netComponent.isHa && (
                  <WithTooltip text={'HA Tunnel'}>
                    {
                      (
                        <div key="ha-tunnel-icon">
                          <CustomIcon
                            src={`${links.imagesBaseUrl}/icons2/icon_rdTunnel.svg`}
                            color=""
                            className={classes.overflow_header__icon}
                          />
                        </div>
                      ) as any
                    }
                  </WithTooltip>
                )}
              </div>
              <div className={cn(classes.info_subtext)}>
                <TextWithTooltip
                  text={text}
                  saveNotification="activity:platform.TEXT_COPY"
                  className={cn(
                    classes.text,
                    isGW && version && classes.text_addition
                  )}
                />
                <TextWithTooltip
                  text={version ? version : ''}
                  saveNotification="activity:platform.TEXT_COPY"
                  className={classes.text}
                />
                {isGW && (
                  <Tooltip
                    title={
                      <span className={classes.tooltip}>
                        {`${netComponent.instanceType}`}
                      </span>
                    }
                  >
                    <div>
                      <CustomIcon
                        src={`${links.imagesBaseUrl}/icons/icon_help.svg`}
                        className={classes.infoIcon}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
            {isGW && netComponent.isGatewayDisabled === true && (
              <WithTooltip text={'Gateway is not available for users'}>
                {
                  (
                    <div key="gateway-disabled">
                      <Text className={classes.gatewayIndication} size={0}>
                        Disabled
                      </Text>
                    </div>
                  ) as any
                }
              </WithTooltip>
            )}
          </div>

          {!isRegion &&
            hasCloudManagementAccessWriteAccess &&
            getDropdownList().length > 0 && (
              <ButtonWithDropdown
                tooltipDisabled
                icon="3dots"
                degRotateImg={90}
                position={'center'}
                color={'#536283'}
                closeOnChange={true}
                className={classes.pointsWrap}
                buttonClass={classes.points}
                show={true}
              >
                {(<BodyDropdownSetting items={getDropdownList()} />) as any}
              </ButtonWithDropdown>
            )}
        </div>
      </div>
    </>
  );
};

export default NetworkWidget;
